import React, { useEffect, useState } from "react";
import { StaticTreeDataProvider } from "react-complex-tree";
import { useMatch } from "react-router-dom";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import { CountTagItem, TagText } from "components/atoms/Tag";
import Tooltip from "components/atoms/Tooltip";
import Alert from "components/atoms/alert/Alert";
import Confirm from "components/atoms/alert/Confirm";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import ContextMenu from "components/molecules/ContextMenu";
import {
    TreeItemChildren,
    TreeItemContainer,
    TreeItemKnob,
    TreeItemMain,
    TreeItemTitle,
    allRefsTreeDataProvider,
} from "components/molecules/tree/Tree";
import TreeEnvironmentWrapper from "components/molecules/tree/TreeEnvironmentWrapper";
import TagEditModal from "components/page/modal/TagEditModal";

import {
    useTagBulkDelete,
    useTagChangeOrder,
    useTagCreate,
    useTagReferenceCreate,
    useTagUpdate,
    useTags,
} from "hooks/queries/useBibliographies";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
`;

const BodyContainer = styled.div`
    overflow-y: auto;
`;

const IconBox = styled.div`
    display: flex;
    align-items: center;
    margin: 0 8px 0 8px;
    width: 10px;
    height: 24px;
`;

const IconWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    border-radius: 2px;

    &:hover {
        background-color: var(--color-ButtonHover4);
    }
`;

const LoadingWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    margin-top: 30px;
`;

const TitleWrapper = styled.div`
    display: flex;
    align-items: center;
    overflow: hidden;
`;

const TextWrapper = styled.div`
    flex: 1;
    overflow: hidden;
`;

const ItemText = styled(Text)`
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 1.4rem;
    font-weight: ${(props) => (props.$isSelected ? "500" : "400")};
    color: ${(props) => (props.$isSelected ? "var(--color-Black)" : "var(--color-SubBlack)")};
`;

function ReferenceFilterTagView(props) {
    // props:
    // viewState
    // onSelectItems
    // treeId
    const [tags, setTags] = useState({
        root: {
            index: "root",
            name: "root",
        },
    });

    const [showTooltip, setShowTooltip] = useState(false);

    const tagQuery = useTags();
    const tagUpdate = useTagUpdate();
    const tagCreate = useTagCreate();
    const tagDelete = useTagBulkDelete();
    const tagChangeOrder = useTagChangeOrder();
    const [viewState, setViewState] = useState({
        [props.treeId]: {
            selectedItems: [],
        },
    });
    const { setToast } = useToast();

    useEffect(() => {
        const selectedItems = props.searchParams?.tags ? props.searchParams?.tags.split(",") : [];

        setViewState((prevState) => ({
            ...prevState,
            [props.treeId]: {
                ...prevState[props.treeId],
                selectedItems: selectedItems,
            },
        }));
    }, [props.searchParams]);

    useEffect(() => {
        if (tagQuery.data) {
            console.log("tagQuery.data", tagQuery.data);
            const items = {};

            const sortedTags = [...tagQuery.data].sort((a, b) => a.order - b.order);

            // 아이템을 `index`를 키로 하여 맵에 저장
            tagQuery.data.forEach((tag) => {
                items[tag.id] = {
                    index: String(tag.id),
                    name: tag.name,
                    order: tag.order,
                    parent: "root",
                    isFolder: false,
                    tagged_count: tag.tagged_count,
                    children: [],
                };
            });

            items["root"] = {
                index: "root",
                name: "root",
                children: sortedTags.map((tag) => String(tag.id)),
            };

            setTags(items);
        }
    }, [tagQuery.data]);

    useEffect(() => {
        const allNodeIds = Object.keys(tags);
        dataProvider.onDidChangeTreeDataEmitter.emit(allNodeIds);
    }, [tags]);

    const dataProvider = new StaticTreeDataProvider(tags, (item, name) => ({
        ...item,
        name,
    }));

    const removeItem = (itemIds) => {
        if (itemIds.length === 0) return;
        Confirm("warn", "태그 삭제", "태그를 삭제하시겠습니까? 태그만 삭제되며, 파일은 유지됩니다.", "삭제", () => {
            tagDelete.mutate(
                {
                    ids: Array.from(itemIds),
                },
                {
                    onSuccess: (data, variables, context) => {
                        setToast("태그가 삭제되었습니다.", "info");
                        props.onSelectItems(
                            viewState[props.treeId].selectedItems.filter((item) => !itemIds.includes(item)),
                        );
                    },
                    onError: (error) => {
                        setToast("태그 삭제에 실패했습니다. 잠시후 시도해주세요.", "error");
                    },
                },
            );
        });
    };

    const isPathReference = useMatch("/:groupId/references");
    const isFilterEmpty = !props.searchParams?.folder && !props.searchParams?.tags && isPathReference !== null;
    const [showContextMenu, setShowContextMenu] = useState({
        index: null,
        x: 0,
        y: 0,
    });

    //region: 참고문헌 드래그 앤 드롭
    const [referenceDragIndex, setReferenceDragIndex] = useState(null);
    const [referenceDragAll, setReferenceDragAll] = useState(false);

    const isReferenceDragEvent = (e) => {
        const dataTransfer = e.dataTransfer;
        if (!dataTransfer) {
            return false;
        }

        const dragTypes = Array.from(dataTransfer.types || []);
        if (dragTypes.includes("application/json")) {
            return true;
        }

        const data = dataTransfer.getData("application/json");
        if (!data) {
            return false;
        }

        try {
            const dragData = JSON.parse(data);
            return dragData.references && Array.isArray(dragData.references);
        } catch (err) {
            return false;
        }
    };

    const referenceTagCreate = useTagReferenceCreate();
    const handleReferenceDrop = (referenceIds, targetTag) => {
        referenceTagCreate.mutate(
            {
                tag_ids: targetTag ? [targetTag.index] : [],
                reference_ids: referenceIds,
                recommended_tags: [],
                delete_old_tag: targetTag ? false : true,
            },
            {
                onSuccess: () => {
                    setToast("선택한 태그가 적용되었습니다.", "info");
                },
                onError: (error) => {
                    Alert("warn", "태그 연결 실패", "태그 연결에 실패했습니다.");
                },
            },
        );
    };
    //endregion

    //region: 마크업 시작
    return (
        <Container>
            <BodyContainer>
                {tagQuery.isLoading ? (
                    <LoadingWrap>
                        <Icon name={"load"} size={"40px"} />
                    </LoadingWrap>
                ) : (
                    <>
                        <TreeEnvironmentWrapper
                            treeId={"allOnlyTree"}
                            dataProvider={allRefsTreeDataProvider}
                            viewState={{
                                allOnlyTree: {
                                    selectedItems: [],
                                    expandedItems: ["root"],
                                },
                            }}
                            multiSelect={false}
                            canDropOnFolder={false}
                            canDropOnNonFolder={true}
                            renderItem={({ item, info, title, arrow, depth, context, children }) => (
                                <TreeItemContainer
                                    depth={0}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        props.onSelectItems([]);
                                    }}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setReferenceDragAll(true);
                                    }}
                                    onDragLeave={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setReferenceDragAll(false);
                                    }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setReferenceDragAll(false);

                                        // 드래그 소스에서 JSON 형식의 데이터를 가져옵니다.
                                        const data = e.dataTransfer.getData("application/json");
                                        if (data) {
                                            try {
                                                const dragData = JSON.parse(data);
                                                // 드래그 데이터에 references 속성이 있다면 참고문헌 드롭으로 판단
                                                if (dragData.references && Array.isArray(dragData.references)) {
                                                    handleReferenceDrop(dragData.references, null);
                                                    return;
                                                }
                                            } catch (err) {
                                                console.error("drop data error", err);
                                            }
                                        }
                                    }}
                                >
                                    <TreeItemMain isDraggingOver={referenceDragAll} isSelected={isFilterEmpty}>
                                        <TreeItemTitle>
                                            <IconBox>
                                                {isFilterEmpty ? (
                                                    <Icon name={"check"} size={"10px"} color={"var(--color-DarkKey)"} />
                                                ) : (
                                                    <Icon name={"tag"} size={"10px"} color={"#9FA8AB"} />
                                                )}
                                            </IconBox>
                                            <ItemText size={"regular"} $isSelected={isFilterEmpty}>
                                                모든 참고문헌
                                            </ItemText>
                                        </TreeItemTitle>
                                    </TreeItemMain>
                                </TreeItemContainer>
                            )}
                        />
                        <TreeEnvironmentWrapper
                            treeId={props.treeId}
                            dataProvider={dataProvider}
                            viewState={viewState}
                            onSelectItems={(selectedItems) => {
                                props.onSelectItems(selectedItems);
                            }}
                            onDrop={async (updatedNodes) => {
                                try {
                                    await tagChangeOrder.mutateAsync(updatedNodes);
                                    setToast("태그 순서가 변경되었습니다.", "info");
                                } catch (error) {
                                    console.log(error);
                                    Alert("error", "태그 순서 변경 중 오류가 발생했습니다.");
                                }
                            }}
                            renderItem={({ item, info, title, arrow, depth, context, children }) => {
                                return (
                                    <TreeItemContainer
                                        {...context.itemContainerWithChildrenProps}
                                        depth={Number(depth)}
                                        onMouseLeave={() =>
                                            setShowContextMenu({
                                                index: null,
                                                x: 0,
                                                y: 0,
                                            })
                                        }
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            setReferenceDragIndex(null);

                                            // 드래그 소스에서 JSON 형식의 데이터를 가져옵니다.
                                            const data = e.dataTransfer.getData("application/json");
                                            if (data) {
                                                try {
                                                    const dragData = JSON.parse(data);
                                                    // 드래그 데이터에 references 속성이 있다면 참고문헌 드롭으로 판단
                                                    if (dragData.references && Array.isArray(dragData.references)) {
                                                        e.stopPropagation();
                                                        handleReferenceDrop(dragData.references, item);
                                                        return;
                                                    }
                                                } catch (err) {
                                                    console.error("drop data error", err);
                                                }
                                            }
                                        }}
                                        onDragOver={(e) => {
                                            e.preventDefault();

                                            // 외부 참고문헌 드래그에만 전파를 차단하고,
                                            // 태그 자체 드래그는 react-complex-tree가 위치를 계산하도록 그대로 둔다.
                                            if (isReferenceDragEvent(e)) {
                                                setReferenceDragIndex(item.index);
                                                e.stopPropagation();
                                            } else if (referenceDragIndex !== null) {
                                                setReferenceDragIndex(null);
                                            }
                                        }}
                                        onDragLeave={(e) => {
                                            e.preventDefault();
                                            if (isReferenceDragEvent(e)) {
                                                setReferenceDragIndex(null);
                                            }
                                        }}
                                    >
                                        <TreeItemMain
                                            {...context.itemContainerWithoutChildrenProps}
                                            {...context.interactiveElementProps}
                                            isDraggingOver={context.isDraggingOver || referenceDragIndex === item.index}
                                            isSelected={context.isSelected}
                                        >
                                            <TreeItemTitle>
                                                <IconBox>
                                                    {context.isSelected ? (
                                                        <Icon
                                                            name={"check"}
                                                            size={"10px"}
                                                            color={"var(--color-DarkKey)"}
                                                        />
                                                    ) : (
                                                        <Icon name={"tag"} size={"10px"} color={"#9FA8AB"} />
                                                    )}
                                                </IconBox>
                                                <TitleWrapper>
                                                    <TextWrapper>
                                                        <ItemText $isSelected={context.isSelected}>{title}</ItemText>
                                                    </TextWrapper>
                                                </TitleWrapper>
                                                <CountTagItem>
                                                    <TagText>{item.tagged_count}</TagText>
                                                </CountTagItem>
                                            </TreeItemTitle>
                                            <TreeItemKnob
                                                className={`tree-knob-${item.index}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setShowContextMenu(
                                                        item.index === showContextMenu.index
                                                            ? { index: null, x: 0, y: 0 }
                                                            : {
                                                                  index: item.index,
                                                                  x: e.currentTarget.getBoundingClientRect().left,
                                                                  y: e.currentTarget.getBoundingClientRect().bottom,
                                                              },
                                                    );
                                                }}
                                            >
                                                {showTooltip ? (
                                                    <Tooltip message={"더보기"}>
                                                        <IconWrap
                                                            onMouseEnter={() => setShowTooltip(true)}
                                                            onMouseLeave={() => setShowTooltip(false)}
                                                        >
                                                            <Icon name={"submenu"} />
                                                        </IconWrap>
                                                    </Tooltip>
                                                ) : (
                                                    <IconWrap
                                                        onMouseEnter={() => setShowTooltip(true)}
                                                        onMouseLeave={() => setShowTooltip(false)}
                                                    >
                                                        <Icon name={"submenu"} />
                                                    </IconWrap>
                                                )}
                                                {showContextMenu.index === item.index && (
                                                    <ContextMenu
                                                        show={true}
                                                        position={{ x: showContextMenu.x, y: showContextMenu.y - 20 }}
                                                        minWidth={"70px"}
                                                        items={[
                                                            {
                                                                name: "이름 변경",
                                                                action: () => {
                                                                    props.setShowTagAddModal(item);
                                                                },
                                                            },
                                                            {
                                                                name: "삭제",
                                                                action: () => {
                                                                    removeItem([item.index]);
                                                                },
                                                            },
                                                        ]}
                                                        onClose={() =>
                                                            setShowContextMenu({
                                                                index: null,
                                                                x: 0,
                                                                y: 0,
                                                            })
                                                        }
                                                        triggerClassName={`tree-knob-${item.index}`}
                                                    />
                                                )}
                                            </TreeItemKnob>
                                        </TreeItemMain>
                                        <TreeItemChildren>{children}</TreeItemChildren>
                                    </TreeItemContainer>
                                );
                            }}
                        />
                    </>
                )}
            </BodyContainer>
            {props.showTagAddModal && (
                <TagEditModal
                    exitModal={(e) => props.setShowTagAddModal(false)}
                    item={props.showTagAddModal}
                    mutation={props.showTagAddModal === true ? tagCreate : tagUpdate}
                />
            )}
        </Container>
    );
}

export default ReferenceFilterTagView;
